import { CoreError, assertSafeInt } from './errors'
import type { Paisa } from './money'
import type { Milli } from './qty'
import { mulDivRound } from './rounding'

/**
 * Line arithmetic. Must match the CHECK constraints on sales_invoice_lines /
 * credit_note_lines (supabase/migrations/20260927000400_billing.sql):
 *   gross    = round(qty_milli * unit_price / 1000)
 *   net      = gross - discount
 *   exclusive: taxable = net,                             tax = round(taxable * bp / 10000)
 *   inclusive: taxable = round(net * 10000 / (10000+bp)), tax = net - taxable
 *   total    = taxable + tax
 * Rounding is half away from zero (Postgres round(numeric)).
 */
export interface LineInput {
  qtyMilli: Milli
  unitPricePaisa: Paisa
  /** Absolute discount on this line, 0 ≤ discount ≤ gross. */
  discountPaisa?: Paisa
  /** Basis points: 1300 = 13%. */
  taxRateBp: number
  priceIncludesTax: boolean
}

export interface LineAmounts {
  grossPaisa: Paisa
  discountPaisa: Paisa
  taxablePaisa: Paisa
  taxPaisa: Paisa
  totalPaisa: Paisa
}

export function lineGross(qtyMilli: Milli, unitPricePaisa: Paisa): Paisa {
  return mulDivRound(qtyMilli, unitPricePaisa, 1000)
}

export function computeLine(input: LineInput): LineAmounts {
  const { qtyMilli, unitPricePaisa, taxRateBp, priceIncludesTax } = input
  const discountPaisa = input.discountPaisa ?? 0
  assertSafeInt(qtyMilli, 'qtyMilli')
  assertSafeInt(unitPricePaisa, 'unitPricePaisa')
  assertSafeInt(discountPaisa, 'discountPaisa')
  if (qtyMilli <= 0) throw new CoreError('invalid_line', 'quantity must be positive')
  if (unitPricePaisa < 0) throw new CoreError('invalid_line', 'price cannot be negative')
  if (!Number.isInteger(taxRateBp) || taxRateBp < 0 || taxRateBp > 10000) {
    throw new CoreError('invalid_line', 'tax rate must be 0–10000 basis points')
  }

  const grossPaisa = lineGross(qtyMilli, unitPricePaisa)
  if (discountPaisa < 0 || discountPaisa > grossPaisa) {
    throw new CoreError('invalid_line', 'discount must be between 0 and the line amount')
  }
  const net = grossPaisa - discountPaisa

  let taxablePaisa: Paisa
  let taxPaisa: Paisa
  if (priceIncludesTax) {
    taxablePaisa = mulDivRound(net, 10000, 10000 + taxRateBp)
    taxPaisa = net - taxablePaisa
  } else {
    taxablePaisa = net
    taxPaisa = mulDivRound(taxablePaisa, taxRateBp, 10000)
  }
  return { grossPaisa, discountPaisa, taxablePaisa, taxPaisa, totalPaisa: taxablePaisa + taxPaisa }
}

/** Discount from a percentage in basis points (1000 = 10%) of the line's gross. */
export function discountFromPercent(grossPaisa: Paisa, percentBp: number): Paisa {
  if (!Number.isInteger(percentBp) || percentBp < 0 || percentBp > 10000) {
    throw new CoreError('invalid_input', 'discount percent must be 0–10000 basis points')
  }
  return mulDivRound(grossPaisa, percentBp, 10000)
}
